import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  IconArrowsShuffle,
  IconLayoutDashboard,
  IconMessageChatbot,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';
import { StyledButton } from '../ui';

/**
 * @typedef {Object} PricingTier
 * @property {string} name - Tier name
 * @property {string} description - Tier description
 * @property {string} price - Tier price or status
 * @property {string[]} [features] - List of features
 * @property {string} ctaText - CTA button text
 * @property {string} ctaHref - CTA button link
 * @property {boolean} [highlighted] - Whether this tier is highlighted
 * @property {string} [badge] - Optional badge text
 */

/**
 * @typedef {Object} PricingPreviewProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 * @property {PricingTier[]} [tiers] - Pricing tiers
 */

/**
 * Strips the description portion from a feature string.
 * Removes everything after " — ", " - ", or " – " dashes.
 *
 * @param {string} feature - Full feature text
 * @returns {string} Short feature name only
 */
function getShortFeature(feature) {
  const dashPattern = / [—–-] /;
  const dashIndex = feature.search(dashPattern);
  if (dashIndex !== -1) {
    return feature.substring(0, dashIndex);
  }
  return feature;
}

/**
 * Checks whether a feature has a longer description after the dash.
 *
 * @param {string} feature - Full feature text
 * @returns {boolean} True if feature has a description after a dash
 */
function hasFeatureDetail(feature) {
  const dashPattern = / [—–-] /;
  return dashPattern.test(feature);
}

/**
 * Pricing Preview Section
 * Inspired by Mercury's simplicity.
 * Features a card flip interaction to reveal full feature details.
 *
 * @param {PricingPreviewProps} props - Component props
 * @returns {JSX.Element} Pricing preview section
 */
function PricingPreview({
  sectionId = 'pricing',
  headline = 'Business Intelligence Delivered',
  subheadline = '',
  tiers = [
    {
      name: 'Team',
      description:
        'FinOps practitioners, FP&A teams, and engineering leaders share the same intelligent view of AWS spend management and financial analysis. With Custom Chat Agents and enriched data sets, your team spends less time wrangling spreadsheets and more time making decisions that matter.',
      price: '',
      features: [
        'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
        '10 Finance-First interactive dashboards — purpose-built for financial decision-making',
        'Up to 5 data sources — enrich your cost and usage reports with your financial data',
        '3 workflows - automate a financial workflow for AWS spend reporting',
        '3 Chat Agents - your team defines the data, we create the source of truth, everyone gets answers they can verify and trust',
        '1 Integration – connect EllaFin™ into your current financial workflow',
        '5 included users',
      ],
      ctaText: 'Join Wait List',
      ctaHref: '/early-access',
      badge: null,
    },
    {
      name: 'Individual',
      description:
        'Delivers a personal, finance-first analytics experience that aligns your AWS cost data with your current financial workflow and reports. Rather than handing you a tool and wishing you luck, we put ready-made insights in front of you from day one. Perfect for FinOps practitioners, finance leaders, and technical owners who own AWS spend management and reporting.',
      price: '',
      features: [
        'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
        '5 Finance-First interactive dashboards — purpose-built for financial decision-making',
        'Up to 3 data sources — enrich your cost and usage reports with your financial data',
        '1 workflow - automate a financial workflow for AWS spend reporting',
        '1 included user',
      ],
      ctaText: 'Get Early Access',
      ctaHref: '/early-access',
      highlighted: true,
      badge: 'Early Access',
    },
    {
      name: 'Department',
      description:
        'FP&A leaders and finance teams need the depth to account for, report on, and forecast cloud costs with the same rigor as every other line item. Integrate variable AWS spend into your existing financial structure and reporting.',
      price: '',
      features: [
        'Standard cost optimization dashboards — pre-built views that surface savings opportunities and spending patterns automatically',
        '10 Finance-First interactive dashboards — purpose-built for financial decision-making',
        'Up to 5 data sources — enrich your cost and usage reports with your financial data',
        '3 workflows — automate a financial workflow for AWS spend reporting',
        '3 Chat Agents — your team defines the source of truth, everyone gets answers they can verify and trust',
        'Advanced Automation — multi-step workflows with 3rd party systems',
        '2 integrations — connect EllaFin™ into your current financial workflows',
        "1 Power User Access — build custom agents and automated workflows tailored to your department's needs",
        '15 included users',
      ],
      ctaText: 'Join Wait List',
      ctaHref: '/early-access',
      badge: null,
    },
  ],
}) {
  const [isVisible, setIsVisible] = useState(false);
  /** @type {[Object<number, boolean>, Function]} Tracks which cards are flipped by index */
  const [flippedCards, setFlippedCards] = useState({});
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /**
   * Toggle the flip state of a pricing card.
   * When flipping to the back (details) side, scrolls the card into view.
   *
   * @param {number} index - The card index to toggle
   * @param {HTMLElement} [cardElement] - The outer card container element to scroll into view
   */
  const handleFlip = useCallback((index, cardElement) => {
    setFlippedCards((prev) => {
      const willFlipToBack = !prev[index];
      if (willFlipToBack && cardElement) {
        // Reset the back face scroll position to the top
        requestAnimationFrame(() => {
          const backFace = cardElement.querySelector('[data-back-face]');
          if (backFace) {
            backFace.scrollTop = 0;
          }
        });
      }
      return { ...prev, [index]: willFlipToBack };
    });
  }, []);

  /**
   * Styles for the "See Details" and "Go Back" text links.
   * @param {boolean} isHighlighted - Whether the tier is highlighted
   * @returns {Object} MUI sx styles
   */
  const getFlipLinkStyles = (isHighlighted) => ({
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: isHighlighted
      ? 'rgba(255, 255, 255, 0.7)'
      : alpha(theme.palette.primary.main, 0.6),
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    alignSelf: 'flex-start',
    fontFamily: 'inherit',
    '&:hover': {
      color: isHighlighted ? '#ffffff' : theme.palette.primary.main,
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: '2px',
      borderRadius: '2px',
    },
  });

  /**
   * Render a feature list item with the triangle bullet.
   * @param {string} text - Feature text to display
   * @param {boolean} isHighlighted - Whether parent card is highlighted
   * @param {number} featureIndex - Index for React key
   * @returns {JSX.Element} Feature list item
   */
  const renderFeatureItem = (text, isHighlighted, featureIndex) => (
    <Box
      component="li"
      key={featureIndex}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.5,
        color: isHighlighted ? 'rgba(255, 255, 255, 0.9)' : '#4a4a4a',
        fontSize: '0.9rem',
      }}
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          backgroundColor: theme.palette.secondary.main,
          clipPath: 'polygon(0 0, 100% 0, 0 100%)',
          mt: 0.75,
          flexShrink: 0,
        }}
      />
      {text}
    </Box>
  );

  /**
   * Render a pricing card with flip interaction.
   * Front shows short feature names; back shows full descriptions.
   * Uses CSS 3D transforms with the front face in normal flow to
   * establish container height, and the back face absolutely positioned.
   *
   * @param {PricingTier} tier - Pricing tier data
   * @param {number} index - Tier index
   * @returns {JSX.Element} Pricing card with flip
   */
  const renderPricingCard = (tier, index) => {
    const isHighlighted = !!tier.highlighted;
    const isFlipped = !!flippedCards[index];
    const hasDetails = tier.features?.some((f) => hasFeatureDetail(f)) ?? false;

    /** Shared card surface styles */
    const cardSurface = {
      backgroundColor: isHighlighted
        ? theme.palette.primary.main
        : theme.palette.background.paper,
      borderRadius: '24px',
      p: { xs: 3, md: 4 },
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isHighlighted
        ? '0 12px 40px rgba(26, 26, 26, 0.25)'
        : '0 4px 24px rgba(0, 0, 0, 0.06)',
      border: isHighlighted ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    };

    return (
      <Box
        key={index}
        data-pricing-card
        sx={{
          flex: 1,
          minWidth: { xs: '100%', sm: '280px' },
          maxWidth: { xs: '100%', md: '360px' },
          perspective: '1200px',
          zIndex: isHighlighted ? 1 : 0,
          transform: isHighlighted && !isMobile ? 'scale(1.05)' : 'none',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform:
              isHighlighted && !isMobile ? 'scale(1.07)' : 'translateY(-8px)',
          },
        }}
      >
        {/* 3D flip inner container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.6s ease',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* ===== FRONT FACE ===== */}
          {/* Position: relative so it establishes the container height */}
          <Box
            sx={{
              ...cardSurface,
              position: 'relative',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.6s ease',
              transitionDelay: `${index * 100}ms`,
            }}
          >
            {/* Badge */}
            {tier.badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 24,
                  backgroundColor: theme.palette.secondary.main,
                  color: '#ffffff',
                  px: 2,
                  py: 0.5,
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {tier.badge}
              </Box>
            )}

            {/* Tier Name */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: isHighlighted ? '#ffffff' : theme.palette.primary.main,
                mb: 1,
                fontSize: '1.25rem',
              }}
            >
              {tier.name}
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: isHighlighted ? 'rgba(255, 255, 255, 0.7)' : '#6c757d',
                mb: 3,
                fontSize: '0.9rem',
                lineHeight: 1.6,
              }}
            >
              {tier.description}
            </Typography>

            {/* Price */}
            {tier.price && (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: isHighlighted
                    ? 'rgba(255, 255, 255, 0.95)'
                    : theme.palette.primary.main,
                  mb: 3,
                  fontSize: '1.5rem',
                }}
              >
                {tier.price}
              </Typography>
            )}

            {/* Features (short names only) */}
            {tier.features && tier.features.length > 0 && (
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  mb: hasDetails ? 0 : 4,
                  flex: 1,
                }}
              >
                {tier.features.map((feature, featureIndex) =>
                  renderFeatureItem(
                    getShortFeature(feature),
                    isHighlighted,
                    featureIndex
                  )
                )}
              </Box>
            )}

            {/* See Details link */}
            {hasDetails && (
              <Box
                component="button"
                onClick={(e) => {
                  const cardEl = e.currentTarget.closest('[data-pricing-card]');
                  handleFlip(index, cardEl);
                }}
                aria-label={`See full feature details for ${tier.name} plan`}
                sx={{
                  ...getFlipLinkStyles(isHighlighted),
                  mt: 1,
                  mb: 3,
                }}
              >
                See Details
              </Box>
            )}

            {/* CTA Button */}
            <Box sx={{ mt: 'auto' }}>
              <StyledButton
                variant="outlined"
                size="medium"
                href={tier.ctaHref}
                fullWidth
                sx={
                  isHighlighted
                    ? {
                        color: '#ffffff',
                        borderColor: '#ffffff',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          color: theme.palette.secondary.main,
                          borderColor: '#ffffff',
                          backgroundColor: '#ffffff',
                        },
                      }
                    : {}
                }
              >
                {tier.ctaText}
              </StyledButton>
            </Box>
          </Box>

          {/* ===== BACK FACE ===== */}
          {/* Position: absolute, overlays the front, rotated 180deg */}
          <Box
            data-back-face
            sx={{
              ...cardSurface,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              overflowY: 'auto',
              boxSizing: 'border-box',
              opacity: isVisible ? 1 : 0,
            }}
          >
            {/* Tier Name */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: isHighlighted ? '#ffffff' : theme.palette.primary.main,
                mb: 2,
                fontSize: '1.25rem',
              }}
            >
              {tier.name}
            </Typography>

            {/* Full feature descriptions */}
            {tier.features && tier.features.length > 0 && (
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  mb: 1,
                  flex: 1,
                }}
              >
                {tier.features.map((feature, featureIndex) => (
                  <Box
                    component="li"
                    key={featureIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 1.5,
                      color: isHighlighted
                        ? 'rgba(255, 255, 255, 0.9)'
                        : '#4a4a4a',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: theme.palette.secondary.main,
                        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                        mt: 0.75,
                        flexShrink: 0,
                      }}
                    />
                    {feature}
                  </Box>
                ))}
              </Box>
            )}

            {/* Go Back link */}
            <Box
              component="button"
              onClick={(e) => {
                const cardEl = e.currentTarget.closest('[data-pricing-card]');
                handleFlip(index, cardEl);
              }}
              aria-label={`Go back to ${tier.name} plan summary`}
              sx={{
                ...getFlipLinkStyles(isHighlighted),
                mt: 1,
                mb: 3,
              }}
            >
              Go Back
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="Pricing"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          {subheadline && (
            <Text
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: '#6c757d',
                maxWidth: '450px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              {subheadline}
            </Text>
          )}
        </Box>

        {/* Pricing Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 3, md: 2 },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'stretch' },
            px: { xs: 0, md: 2 },
          }}
        >
          {tiers.map((tier, index) => renderPricingCard(tier, index))}
        </Box>

        {/* Add-Ons */}
        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            textAlign: 'center',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.6s ease 0.4s',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 0.5,
            }}
          >
            Add-Ons
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: '#6c757d',
              mb: 3,
            }}
          >
            Extend any plan with premium capabilities
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {[
              {
                icon: IconMessageChatbot,
                label: 'Custom Chat Agents',
              },
              { icon: IconLayoutDashboard, label: 'Custom Dashboards' },
              {
                icon: IconArrowsShuffle,
                label: 'Automated Workflows & 3rd Party Integration',
              },
            ].map((addon, index) => {
              const IconComponent = addon.icon;
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: '9999px',
                    border: `2px solid ${theme.palette.secondary.main}`,
                    color: theme.palette.primary.main,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${theme.palette.secondary.main}1a`,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <IconComponent
                    size={20}
                    stroke={1.8}
                    color={theme.palette.secondary.main}
                  />
                  <span>{addon.label}</span>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default PricingPreview;
